import { Box, Image, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { Query, gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { Loading, Modal } from "@outschool/ui-legacy-component-library";
import React from "react";

import * as inviteLearnersImage from "../../../images/home/invite_learners_to_join.png";
import * as learnTogetherImage from "../../../images/learn_together.png";
import * as SectionFns from "../../../shared/Section";
import {
  EnrolledClassActivity,
  EnrolledClassSection
} from "../enrolled/EnrolledClassTypes";
import ShareContainer from "../sharing/ShareContainer";

interface ShareEnrollmentModalProps {
  open: boolean;
  onClose: () => void;
  activity: EnrolledClassActivity;
  section: EnrolledClassSection;
}

const activityPromotableSectionsQuery = gql`
  query ActivityPromotableSections($activityUid: ID!) {
    activity(uid: $activityUid) {
      promotableSections {
        uid
      }
    }
  }
`;

export default function ShareEnrollmentModal({
  open,
  onClose,
  activity,
  section
}: ShareEnrollmentModalProps) {
  const { currentUser } = useSession();
  const { t } = useTranslation(
    "client\\components\\enrolling\\ShareEnrollmentModal"
  );

  const availableSpaces = SectionFns.availableSpaces(section);
  const enrollmentsNeeded = SectionFns.enrollmentsNeededForMinimum(section);
  const { filledSpaceCount } = section;

  return (
    <Modal open={open} onClose={onClose}>
      <div>
        <Box
          flex
          sx={{
            textAlign: "center",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "360px"
          }}
        >
          <Query
            query={activityPromotableSectionsQuery}
            variables={{ activityUid: activity && activity.uid }}
          >
            {({ loading, data }: $TSFixMe) => {
              if (loading) {
                return <Loading />;
              }
              if (enrollmentsNeeded) {
                return (
                  <ShareMessage
                    sharePromptHeader={t(
                      "This class works best with {{numMinStudents}} to {{numMaxStudents}} learners",
                      {
                        numMinStudents: section.size_min,
                        numMaxStudents: section.size_max
                      }
                    )}
                    enrollmentMessage={t(
                      "There is currently {{filledSpaceCount}} learner enrolled.",
                      {
                        filledSpaceCount,
                        count: filledSpaceCount,
                        defaultValue_plural:
                          "There are currently {{filledSpaceCount}} learners enrolled."
                      }
                    )}
                    enrollmentsNeeded={enrollmentsNeeded}
                  />
                );
              } else if (availableSpaces > 0) {
                return (
                  <ShareMessage
                    sharePromptHeader={t`Want to take this class with a friend?`}
                    enrollmentMessage={
                      filledSpaceCount == 1
                        ? t(
                            "There is currently 1 learner enrolled and {{availableSpaces}} spot remaining.",
                            {
                              availableSpaces,
                              count: availableSpaces,
                              defaultValue_plural:
                                "There is currently 1 learner enrolled and {{availableSpaces}} spots remaining."
                            }
                          )
                        : t(
                            "There are currently {{filledSpaceCount}} learners enrolled and {{availableSpaces}} spot remaining.",
                            {
                              availableSpaces,
                              filledSpaceCount,
                              count: availableSpaces,
                              defaultValue_plural:
                                "There are currently {{filledSpaceCount}} learners enrolled and {{availableSpaces}} spots remaining."
                            }
                          )
                    }
                    enrollmentsNeeded={enrollmentsNeeded}
                  />
                );
              }

              const otherPromotableSections =
                data?.activity?.promotableSections?.filter(
                  (s: $TSFixMe) => s.uid !== section.uid
                ) ?? [];
              const otherSectionsAvailable = otherPromotableSections.length > 0;
              return (
                <ShareMessage
                  sharePromptHeader={t`Have a friend interested in this class?`}
                  enrollmentMessage={
                    otherSectionsAvailable
                      ? t`Spots are still available at other times.`
                      : t`They can message the teacher from the class page to request another time.`
                  }
                  enrollmentsNeeded={enrollmentsNeeded}
                />
              );
            }}
          </Query>
        </Box>
        <ShareContainer
          activity={activity}
          section={section}
          user={currentUser}
          showReferralCTA={false}
          onEmailShareClose={onClose}
          currentUserIsEnrolled={true}
        />
      </div>
    </Modal>
  );
}

interface ShareMessageProps {
  sharePromptHeader: string;
  enrollmentMessage: string;
  enrollmentsNeeded: number;
}

function ShareMessage({
  sharePromptHeader,
  enrollmentMessage,
  enrollmentsNeeded
}: ShareMessageProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\ShareEnrollmentModal"
  );
  return (
    <React.Fragment>
      <Box
        sx={{
          marginBottom: "2em"
        }}
      >
        <Image
          alt={
            enrollmentsNeeded
              ? t`Invite your friends`
              : t`Kids learning together`
          }
          src={enrollmentsNeeded ? inviteLearnersImage : learnTogetherImage}
          sx={{
            width: "65%"
          }}
        />
      </Box>

      <Box
        sx={{
          color: "grey.500",
          marginBottom: "0.5em",
          marginTop: "0.5em"
        }}
      >
        <Typography
          variant="h3"
          sx={{
            color: "grey.900"
          }}
        >
          {sharePromptHeader}
        </Typography>
        {enrollmentMessage}
      </Box>
    </React.Fragment>
  );
}
